import { Button, ButtonGroup } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useErrorMessage } from "../../components/FormError";
import { useAuthenticatedQuery } from "../../lib/api";
import { processFileResponseOrThrow } from "../../lib/file";

/**
 * responseCallback for the form downloads: hand the PDF to the browser.
 *
 * Same shape as `downloadFile` in `reports/Filing13f.tsx`. The throwing variant
 * lets a blob, object-URL or anchor failure reach the caller (APP-1453), and
 * the `true` is there because React Query rejects an undefined query result —
 * which is also why `isError` on the old `useAuthenticatedFileFetch` could not
 * tell a failed request from a successful download.
 */
async function downloadFile(res: Response): Promise<boolean> {
  await processFileResponseOrThrow(res);
  return true;
}

/**
 * One form download. The api route is the query key, which
 * useAuthenticatedQuery turns into the URL.
 *
 * enabled: false leaves the request to `refetch()` from the button. retry:
 * false keeps a failure to one request: the React Query default retries three
 * times with backoff, so a failing click held its error back for seconds and
 * sent the same request to the python form generator again each time
 * (APP-1619).
 */
function useFormDownload(form: string, contactId: string | undefined) {
  return useAuthenticatedQuery<unknown, boolean>({
    queryKey: ["custodian-forms", form, String(contactId)],
    queryFn: async () => true,
    queryOptions: { enabled: false, retry: false },
    responseCallback: downloadFile,
  });
}

const GenerateForms = () => {
  const { contactId } = useParams();
  const { errorMessage, setError, resetError } = useErrorMessage();

  const { refetch: downloadSchwabIra } = useFormDownload(
    "schwab-ira",
    contactId,
  );
  const { refetch: downloadSchwabTaxable } = useFormDownload(
    "schwab-taxable",
    contactId,
  );
  const { refetch: downloadSchwabToa } = useFormDownload(
    "schwab-toa",
    contactId,
  );

  // `refetch` resolves rather than throws when the request fails, so the
  // failure is read off the result. The messages match the ones the api
  // raises for each form (api/src/custodian-forms/custodian-forms.service.ts).
  const generate = async (
    download: typeof downloadSchwabIra,
    failureMessage: string,
  ) => {
    resetError();
    const result = await download();
    if (result.isError) {
      setError(failureMessage);
      console.error(failureMessage, result.error);
    }
  };

  return (
    <>
      <ButtonGroup>
        <Button
          onClick={() =>
            generate(downloadSchwabIra, "Failed to generate Schwab IRA form")
          }
        >
          Generate IRA Form
        </Button>
        <Button
          onClick={() =>
            generate(
              downloadSchwabTaxable,
              "Failed to generate Schwab Taxable Account form",
            )
          }
        >
          Generate Taxable Account Form
        </Button>
        <Button
          onClick={() =>
            generate(downloadSchwabToa, "Failed to generate Schwab TOA form")
          }
        >
          Generate TOA Form
        </Button>
      </ButtonGroup>
      {errorMessage}
    </>
  );
};

export default GenerateForms;
